import type { FacetChoiceChangedPayload, SearchResponseFacet } from '@sitecore-search/data';
import type { RangeFilterDescriptor, RangeSelection } from '@sitecorecomponents/Search/components/RangeFilterDropdown/rangeFilter';
import type { SearchResultsSelectedFilters } from '@sitecorecomponents/Search/types/facets';

/* The search's selections in a shape that outlives the page: facet values by
   their text, because a facet value id is opaque and only valid for the
   response it came in, and ranges as bounds either of which may be open, which
   is how a search agent stores them. This is what goes in the URL and what a
   search agent is built from. */
export interface SearchCriteria {
    values: Record<string, string[]>;
    ranges: Record<string, [number | null, number | null]>;
}

const RANGE_SEPARATOR = '..';

/** `price=4000..9000&city=Aarhus C,Odense` — every other parameter is not ours. */
export function criteriaFromParams(params: URLSearchParams, attributes: readonly string[]): SearchCriteria {
    const criteria: SearchCriteria = { values: {}, ranges: {} };

    for (const attribute of attributes) {
        const raw = params.get(attribute);
        if (!raw) continue;

        if (raw.includes(RANGE_SEPARATOR)) {
            const [from, to] = raw.split(RANGE_SEPARATOR).map(toNumberOrNull);
            if (from !== null || to !== null) criteria.ranges[attribute] = [from, to];
            continue;
        }

        const values = raw.split(',').map(v => v.trim()).filter(Boolean);
        if (values.length > 0) criteria.values[attribute] = values;
    }

    return criteria;
}

/** Rewrites only the given attributes, so `view` and anything else on the URL stays put. */
export function criteriaToParams(criteria: SearchCriteria, base: URLSearchParams, attributes: readonly string[]): URLSearchParams {
    const params = new URLSearchParams(base);
    attributes.forEach(attribute => params.delete(attribute));

    for (const [attribute, values] of Object.entries(criteria.values)) {
        if (values.length > 0) params.set(attribute, values.join(','));
    }
    for (const [attribute, [from, to]] of Object.entries(criteria.ranges)) {
        params.set(attribute, `${from ?? ''}${RANGE_SEPARATOR}${to ?? ''}`);
    }

    return params;
}

export function criteriaFromSelection(
    selectedFacets: SearchResultsSelectedFilters | null | undefined,
    facets: readonly SearchResponseFacet[],
    ranges: RangeSelection,
): SearchCriteria {
    const criteria: SearchCriteria = { values: {}, ranges: { ...ranges } };

    for (const selected of selectedFacets ?? []) {
        if (!('facetValueId' in selected)) continue;
        // The store leaves valueLabel as "" for value-id selections.
        const text = selected.valueLabel
            || facets.find(f => f.name === selected.facetId)?.value.find(v => v.id === selected.facetValueId)?.text;
        if (!text) continue;
        (criteria.values[selected.facetId] ??= []).push(text);
    }

    return criteria;
}

/** Puts the parameters on the address bar without a navigation or a history entry. */
export function replaceSearchParams(params: URLSearchParams): void {
    const query = params.toString();
    const url = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
    if (url !== `${window.location.pathname}${window.location.search}${window.location.hash}`) {
        window.history.replaceState(window.history.state, '', url);
    }
}

/* Turns criteria back into what the widget can apply, against the controls the
   page actually has. A range lands on its slider, open ends taken from the
   slider's bounds; with no slider but a facet of that name - rooms authored as
   checkboxes, say - it selects the facet values inside the range. Anything the
   page has no control for is dropped rather than applied unseen. */
export function resolveCriteria(
    criteria: SearchCriteria,
    facets: readonly SearchResponseFacet[],
    rangeFilters: readonly RangeFilterDescriptor[],
): { facetClicks: FacetChoiceChangedPayload[]; ranges: RangeSelection } {
    const facetClicks: FacetChoiceChangedPayload[] = [];
    const ranges: RangeSelection = {};

    const select = (facet: SearchResponseFacet, matches: (value: SearchResponseFacet['value'][number]) => boolean) => {
        const facetIndex = facets.indexOf(facet);
        facet.value.forEach((value, facetValueIndex) => {
            if (!matches(value)) return;
            facetClicks.push({ type: 'valueId', facetId: facet.name, facetIndex, facetValueId: value.id, facetValueIndex, checked: true });
        });
    };

    for (const [attribute, [from, to]] of Object.entries(criteria.ranges)) {
        const slider = rangeFilters.find(f => f.attribute === attribute);
        if (slider) {
            ranges[attribute] = [from ?? slider.min, to ?? slider.max];
            continue;
        }

        const facet = facets.find(f => f.name === attribute);
        if (facet) {
            select(facet, value => {
                const n = Number(value.min ?? value.text);
                return !Number.isNaN(n) && (from === null || n >= from) && (to === null || n <= to);
            });
        }
    }

    for (const [attribute, texts] of Object.entries(criteria.values)) {
        const facet = facets.find(f => f.name === attribute);
        if (facet) select(facet, value => texts.includes(value.text));
    }

    return { facetClicks, ranges };
}

function toNumberOrNull(value: string | undefined): number | null {
    if (!value) return null;
    const n = Number(value);
    return Number.isNaN(n) ? null : n;
}
